import {
    appStoreIntegrationTypeForPlatform,
    iconAndAltForIntegrationType,
    shouldShowConfigurationDuringOnboarding,
} from '../../helpers/integrationHelpers';
import {
    AppComponentProps,
    CALLER_CONTEXT_PARAM,
    IntegrationsRecommendationBannerData,
    REDIRECT_URL_PARAM,
    SetupStepsCallerContext,
} from '../../typescriptHelpers';
import { antGrey, hexStringToRGBAString, runwayColors, tagStyle } from '../styles/globalStyles';
import Icon, { BulbOutlined } from '@ant-design/icons';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Typography from 'antd/lib/typography';
import * as React from 'react';
import { useOauthIntegrationInstaller, useSetupStepsState } from './hooks';
import { useSelector } from 'react-redux';
import { Transition } from 'react-transition-group';
import {
    selectApp,
    selectAppHasSelectCapabilitiesConfigured,
    selectAppIntegrations,
    selectAppPlatform,
    selectAppStoreType,
    selectIntegrationFromIntegrationType,
    selectIsRunwayOrg,
    selectJoinedCommsProvidersNamesList,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { RelatedIntegrationsSetup } from './RelatedIntegrationsSetup';
import { Link, useHistory } from 'react-router-dom';
import { createAppRoute, createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import {
    allSupportedIntegrationTypesForCapability,
    displayTextForIntegrationStatus,
    tagColorForIntegrationStatus,
} from '../../helpers';
import { IntegrationCapability, IntegrationErrorType, IntegrationType, SettingType } from '../../types';
import { flatMap } from 'lodash';
import appPlaceholderImage from '../../../assets/app-placeholder.png';
import { useUpdateIntegrationModal } from './useUpdateIntegrationModal';
import { CommonSetupFlowLayout } from '../sharedComponents/CommonSetupFlowContainer';
import IntegrationConfiguration from '../IntegrationConfiguration';
import { displayTextForAppPlatform } from '../../helpers/displayHelpers';
import { NavigationBar } from '../sharedComponents/NavigationBar';

interface IntegrationsRecommendationBannerProps extends IntegrationsRecommendationBannerData {
    appId: string;
    orgId: string;
    stepContainerHeight: number;
    showIntegrationSetup: boolean;
    setShowIntegrationSetup: React.Dispatch<React.SetStateAction<boolean>>;
    context?: SetupStepsCallerContext;
}
const IntegrationsRecommendationBannerComponent: React.FC<IntegrationsRecommendationBannerProps> = ({
    appId,
    orgId,
    stepContainerHeight,
    showIntegrationSetup,
    setShowIntegrationSetup,
    type,
    title,
    description,
    actionText,
    integrationCapabilities,
    context,
}) => {
    const recommendedIntegrationsConnected = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, integrationCapabilities),
    );

    React.useEffect(() => {
        if (recommendedIntegrationsConnected) {
            setShowIntegrationSetup(false);
        }
    }, [recommendedIntegrationsConnected]);

    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const duration = 600;

    const setupScreenDefaultStyle: React.CSSProperties = {
        transition: `all ${duration}ms ease-in-out`,
        height: 'auto',
        maxHeight: 0,
        width: '100%',
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
    };

    const setupScreenTransitionStyles = {
        entering: { maxHeight: 0, overflow: 'hidden' },
        entered: { maxHeight: stepContainerHeight, overflow: 'auto' },
        exiting: { maxHeight: stepContainerHeight, overflow: 'auto' },
        exited: { maxHeight: 0, overflow: 'hidden' },
    };

    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);

    return recommendedIntegrationsConnected ? null : (
            <div style={{ position: 'relative', zIndex: 2 }}>
                <div
                    style={{
                        backgroundColor: hexStringToRGBAString(runwayColors.primary, 0.15),
                        minHeight: 100,
                        padding: '24px 38px',
                        gap: 20,
                        display: 'flex',
                    }}
                >
                    <Icon component={BulbOutlined} style={{ color: runwayColors.primary, fontSize: 22 }} />
                    <Space direction="vertical" size={6}>
                        <Typography.Text style={{ color: antGrey.grey8, fontSize: 16, fontWeight: 600 }}>
                            {title(commsProviders)}
                        </Typography.Text>
                        <Typography.Paragraph
                            style={{
                                color: antGrey.grey8,
                                fontSize: 14,
                                fontWeight: 400,
                                marginBottom: 0,
                                maxWidth: 700,
                            }}
                        >
                            {description}{' '}
                            {actionText && (
                                <Button
                                    type="link"
                                    onClick={() => {
                                        setShowIntegrationSetup(true);
                                    }}
                                    style={{
                                        color: runwayColors.primary,
                                        fontWeight: 600,
                                        position: 'revert',
                                        lineHeight: 'revert',
                                        height: 'auto',
                                        padding: 0,
                                    }}
                                >
                                    {actionText}
                                </Button>
                            )}
                        </Typography.Paragraph>
                    </Space>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'end' }}>
                        <Space>
                            {integrationCapabilities.map((integrationCapability, collectionsIndex) => {
                                const isAppStore = integrationCapability === IntegrationCapability.AppStore;
                                const integrations =
                                    isAppStore ?
                                        [appStoreIntegrationTypeForPlatform(platform)]
                                    :   allSupportedIntegrationTypesForCapability(
                                            integrationCapability,
                                            isRunwayOrg,
                                            orgId,
                                        );
                                return (
                                    <React.Fragment key={collectionsIndex}>
                                        {integrations.map((integration) => {
                                            const [icon, alt] = iconAndAltForIntegrationType(integration);
                                            return (
                                                <img key={integration} src={icon} alt={alt} width={30} height={30} />
                                            );
                                        })}
                                        {collectionsIndex !== integrationCapabilities.length - 1 && (
                                            <Divider type="vertical" style={{ borderColor: antGrey.grey6 }} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </Space>
                    </div>
                </div>
                <Transition in={showIntegrationSetup} timeout={200}>
                    {(state) => (
                        <div
                            style={{
                                ...setupScreenDefaultStyle,
                                ...setupScreenTransitionStyles[state],
                            }}
                        >
                            <RelatedIntegrationsSetup
                                appId={appId}
                                orgId={orgId}
                                currentStepType={type}
                                relatedCapabilities={integrationCapabilities}
                                showIntegrationSetup={setShowIntegrationSetup}
                                context={context}
                            />
                        </div>
                    )}
                </Transition>
            </div>
        );
};

export const extraLabel: (integrationType: IntegrationType) => string = (integrationType) => {
    switch (integrationType) {
        case IntegrationType.GitLabVCS:
        case IntegrationType.GitHubVCS:
            return 'VCS';
        case IntegrationType.GitHubCI:
        case IntegrationType.GitLabCI:
            return 'CI';
    }
    return null;
};

export const AssociatedIntegrationsBar: React.FC<{
    orgId: string;
    appId: string;
    appText: string;
    appImageUrl: string;
    integrationCapabilities?: IntegrationCapability[];
}> = ({ appText, appImageUrl, integrationCapabilities, appId, orgId }) => {
    const connectedIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));

    return (
        <Space
            style={{
                backgroundColor: runwayColors.white,
                borderTop: `1px solid ${antGrey.grey4}`,
                height: 88,
                paddingLeft: 32,
                zIndex: 2,
            }}
            size={14}
        >
            <Space size={17}>
                <Avatar
                    shape="square"
                    style={{
                        borderRadius: 6.6,
                        filter: 'var(--rw-filter-drop-shadow)',
                    }}
                    src={appImageUrl || appPlaceholderImage}
                    size={32}
                />
                <Typography.Text style={{ color: antGrey.grey7, fontSize: 14, fontWeight: 600 }}>
                    {appText}
                </Typography.Text>
            </Space>

            {flatMap(integrationCapabilities, (integrationCapability) => {
                let integrationTypes = allSupportedIntegrationTypesForCapability(
                    integrationCapability,
                    isRunwayOrg,
                    orgId,
                );

                // Only show integrations that are connected
                integrationTypes = integrationTypes.filter(
                    (integrationType) => !!connectedIntegrations.find((i) => i.id === integrationType),
                );

                return integrationTypes.map((integrationType) => {
                    const [icon, alt] = iconAndAltForIntegrationType(integrationType);

                    const integration = connectedIntegrations.find((i) => i.id === integrationType);

                    const shouldAllowIncompleteData = !shouldShowConfigurationDuringOnboarding(integrationType);

                    const integrationStatus = displayTextForIntegrationStatus(integration, shouldAllowIncompleteData);
                    const integrationStatusColor = tagColorForIntegrationStatus(integration, shouldAllowIncompleteData);

                    const integrationLabel = extraLabel(integrationType);

                    return (
                        <React.Fragment key={integrationType}>
                            <Divider type="vertical" style={{ borderColor: antGrey.grey6 }} />
                            <Link
                                to={{
                                    pathname: createAppSettingsRouteWithSettingType(
                                        orgId,
                                        appId,
                                        SettingType.Integrations,
                                    ),
                                    state: {
                                        scrollTo: integrationCapability,
                                    },
                                }}
                            >
                                <Space size={18}>
                                    <img src={icon} alt={alt} width={30} height={30} />
                                    <Tag color={integrationStatusColor} style={tagStyle}>
                                        {integrationLabel ? `${integrationLabel} ` : ''}
                                        {integrationStatus}
                                    </Tag>
                                </Space>
                            </Link>
                        </React.Fragment>
                    );
                });
            })}
        </Space>
    );
};

export type SetupStepsContainerProps = AppComponentProps;

export const SetupStepsContainer: React.FC<SetupStepsContainerProps> = (props) => {
    const history = useHistory();

    const { orgId, appId } = props;

    // get caller context from url param
    const callerContext = React.useMemo(() => {
        const params = new URLSearchParams(history.location.search);
        return params.get(CALLER_CONTEXT_PARAM) as SetupStepsCallerContext;
    }, [history.location.search]);

    // get redirect url from url param, it shouldn't contain the origin part
    const redirectUrl = React.useMemo(() => {
        const params = new URLSearchParams(history.location.search);
        const rawParams = params.get(REDIRECT_URL_PARAM);
        const decodedUrlParam = rawParams ? decodeURIComponent(rawParams) : null;
        return decodedUrlParam;
    }, [history.location.search]);

    const lastInstalledIntegrationType = history.location.state?.['lastInstalledIntegrationType'];

    const lastInstalledIntegration = useSelector((state: RootState) =>
        selectIntegrationFromIntegrationType(state, appId, lastInstalledIntegrationType),
    );

    React.useEffect(() => {
        if (
            lastInstalledIntegrationType &&
            lastInstalledIntegration &&
            shouldShowConfigurationDuringOnboarding(lastInstalledIntegrationType) &&
            lastInstalledIntegration?.integrationError?.status === IntegrationErrorType.IncompleteData
        ) {
            setShowUpdateIntegrationModal(true);
        }
    }, [lastInstalledIntegrationType, lastInstalledIntegration]);

    //handles configuring existing integration
    const { setShowUpdateIntegrationModal, updateIntegrationModalProps } = useUpdateIntegrationModal({
        orgId,
        appId,
        selectedIntegration: lastInstalledIntegration,
    });

    // to intercept oauth installation redirects
    useOauthIntegrationInstaller({});

    const [showIntegrationSetup, setShowIntegrationSetup] = React.useState(false);

    const app = useSelector((state: RootState) => selectApp(state, appId));

    const { currentSetupStep, goToNextStep, goToPreviousStep, hasNextStep, hasPreviousStep } = useSetupStepsState(
        appId,
        callerContext,
    );

    const { title, description, icon, integrationsRecommendationBannerData, SetupViewComponent, InfoViewComponent } =
        currentSetupStep ?? {};

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const navTitle = typeof title === 'string' ? title : title(platform, appStoreType);
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);
    const navDescription = description(commsProviders);
    const navIcon = icon(platform);

    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);

    React.useEffect(() => {
        // reset confirmation modals when steps change
        setHasUnsavedChanges(false);
    }, [currentSetupStep]);

    return (
        currentSetupStep && (
            <CommonSetupFlowLayout
                navigationBar={
                    <NavigationBar
                        canContinue
                        title={navTitle}
                        description={navDescription}
                        icon={navIcon}
                        isFinalStep={!hasNextStep}
                        canGoBack={hasPreviousStep}
                        onContinue={goToNextStep}
                        onGoBack={goToPreviousStep}
                        shouldAskForConfirmation={hasUnsavedChanges}
                        onClose={() => {
                            if (redirectUrl) {
                                history.replace(redirectUrl);
                            } else {
                                history.push(createAppRoute(orgId, appId), { shouldShowSuccessBanner: true });
                            }
                        }}
                    />
                }
                banner={({ contentHeight }) => (
                    <IntegrationsRecommendationBannerComponent
                        {...integrationsRecommendationBannerData}
                        appId={appId}
                        orgId={orgId}
                        stepContainerHeight={contentHeight}
                        showIntegrationSetup={showIntegrationSetup}
                        setShowIntegrationSetup={setShowIntegrationSetup}
                        context={callerContext}
                    />
                )}
                sideBar={!!InfoViewComponent && <InfoViewComponent {...props} />}
                footer={
                    <>
                        <AssociatedIntegrationsBar
                            orgId={orgId}
                            appId={appId}
                            appText={`${app.appSettings.appName} (${displayTextForAppPlatform(
                                app.appSettings.platform,
                                app.appSettings.appStoreType,
                            )})`}
                            appImageUrl={app.appSettings.appImageUrl}
                            integrationCapabilities={integrationsRecommendationBannerData?.integrationCapabilities}
                        />
                        {lastInstalledIntegration && (
                            <IntegrationConfiguration
                                {...updateIntegrationModalProps}
                                onClose={() => {
                                    setShowUpdateIntegrationModal(false);
                                }}
                            />
                        )}
                    </>
                }
            >
                {SetupViewComponent && (
                    <SetupViewComponent
                        {...props}
                        context={callerContext}
                        onChange={(newHasUnsavedChanges) => setHasUnsavedChanges(newHasUnsavedChanges)}
                        displayRelatedIntegrationsSetup={() => setShowIntegrationSetup(true)}
                    />
                )}
            </CommonSetupFlowLayout>
        )
    );
};
